import React, {useEffect} from 'react';
import {ClientProfileReportTableFull} from "./ClientProfileReportTable";
import {
    AuthorizationInfo, CLIENT_PROFILE_CLIENT_REPORTS_PATH,
    ClientReportInfo,
    ClientReportReportStatusMapWithDefault,
    ClientReportReportTypeMapWithDefault
} from "./domain";
import {clientReportReducer, initState} from "./clientReportReducer";
import {FatOutlineButton} from "../../common/component/FatOutlineButton";
import {ClientReportNewModal} from "./ClientReportNewModal";
import {ClientReportViewModal} from "./ClientReportViewModal";
import {ErrorDisplay, RelayConnection} from "../../common";
import {gql, useQuery} from '@apollo/client';
import {RelayPagination, useRelayPagination} from "../pagination";
import {Pane, Switch} from 'evergreen-ui';
import {useSearchParam} from "../../common/hook";
import {SimpleDropdown} from "common";
import {useLocation} from "react-router";

export const CLIENT_REPORTS_QUERY = gql`
query clientReportsQuery($first: Int = 20, $after: String, $clientGuid: String!, $params: ClientReportQueryParamsInput!) {
    clientReportsQuery(
        first: $first
        after: $after
        clientGuid: $clientGuid
        params: $params
    ) {
        edges {
            cursor
            node {
                guid
                reportType
                reportStatus 
                newAuthorizationStartDate
                finalDiagnosticContactDate
                vinelandIncluded
                firstTreatmentOfferedDate
                lastDayOfServiceBilled
                anticipatedDischargeDate
                serviceRecommended
                serviceType
                caregiverMidLevelHoursMin
                caregiverMidLevelHoursMax
                caregiverHighLevelHoursMin
                caregiverHighLevelHoursMax
                practitionerParaprofessionalHoursMin
                practitionerParaprofessionalHoursMax
                practitionerMidLevelHoursMin
                practitionerMidLevelHoursMax
                practitionerHighLevelHoursMin
                practitionerHighLevelHoursMax
                practitionerGroupCareHoursMin
                practitionerGroupCareHoursMax
                offeredParaprofessionalHours
                offeredMidLevelHours
                offeredHighLevelHours
                offeredGroupCareHours
                treatmentOffered
                
                authorization {
                    bhpnAuthNumber
                    reportDueDate
                }
                authorizedDate 
                authorizedBy 
                clientProfile {
                    name
                    medicalRecordNumber
                }
                clientReportNotifyUsers {
                  id
                  guid
                  firstName
                  lastName
                }
                clientReportFiles {
                    guid
                    fileName
                    fileExtension
                    downloadUrl
                }
                clientReportFilesBySfOperationCase {
                    guid
                    fileName
                    fileExtension
                    downloadUrl
                }
                submittedAt
                submittedBy {
                  id
                  guid
                  firstName
                  lastName
                }
                sfOperationCaseNumber
                sfOperationCaseId
                sfOperationCaseRptStatus
                sfCloseReason
                clinicalRationale
                nameAndTitle
                licenseNumber
                sfCloseDate
                sfLastModifiedDate
                shared
            }
        }
        totalElements
        pageInfo {
            hasPreviousPage
            endCursor
            hasNextPage
            startCursor
        }
    }
    authorizationsByClientGuidQuery(clientGuid: $clientGuid) {
        status
        guid
        sfId
        recordType
        bhpnAuthNumber
        numOfTier
        
        isDraft1
        status1
        serviceType1
        startDate1
        endDate1
        directCareBeneficialHour1
        midLevelBeneficialHour1
        highLevelBeneficialHour1
        groupCareBeneficialHour1
        directCareHour1
        midLevelHour1
        highLevelHour1
        groupCareHour1
        directCareCode1
        midLevelCode1
        highLevelCode1
        groupCareCode1

        status2
        isDraft2
        serviceType2
        startDate2
        endDate2
        directCareBeneficialHour2
        midLevelBeneficialHour2
        highLevelBeneficialHour2
        groupCareBeneficialHour2
        directCareHour2
        midLevelHour2
        highLevelHour2
        groupCareHour2
        directCareCode2
        midLevelCode2
        highLevelCode2
        groupCareCode2

        status3
        isDraft3
        serviceType3
        startDate3
        endDate3
        directCareBeneficialHour3
        midLevelBeneficialHour3
        highLevelBeneficialHour3
        groupCareBeneficialHour3
        directCareHour3
        midLevelHour3
        highLevelHour3
        groupCareHour3
        directCareCode3
        midLevelCode3
        highLevelCode3
        groupCareCode3

        status4
        isDraft4
        serviceType4
        startDate4
        endDate4
        directCareBeneficialHour4
        midLevelBeneficialHour4
        highLevelBeneficialHour4
        groupCareBeneficialHour4
        directCareHour4
        midLevelHour4
        highLevelHour4
        groupCareHour4
        directCareCode4
        midLevelCode4
        highLevelCode4
        groupCareCode4

        status5
        isDraft5
        serviceType5
        startDate5
        endDate5
        directCareBeneficialHour5
        midLevelBeneficialHour5
        highLevelBeneficialHour5
        groupCareBeneficialHour5
        directCareHour5
        midLevelHour5
        highLevelHour5
        groupCareHour5
        directCareCode5
        midLevelCode5
        highLevelCode5
        groupCareCode5

        status6
        isDraft6
        serviceType6
        startDate6
        endDate6
        directCareBeneficialHour6
        midLevelBeneficialHour6
        highLevelBeneficialHour6
        groupCareBeneficialHour6
        directCareHour6
        midLevelHour6
        highLevelHour6
        groupCareHour6
        directCareCode6
        midLevelCode6
        highLevelCode6
        groupCareCode6
        latestSubmittedClientReport {
            guid
            submittedAt
            reportType
            reportStatus 
            sfOperationCaseNumber
            sfCloseReason
            sfCloseDate
        }

        latestSubmittedClientReportByReportType {
            guid
            reportType
            authorizedDate
            reportStatus
            sfOperationCaseNumber
            submittedAt
            sfCloseReason
            sfCloseDate
        }
    }
}
`

export type ClientReportsQueryResponse = RelayConnection<ClientReportInfo> & { totalElements: number };
export type AuthorizationListResponse = AuthorizationInfo[];

export type ClientProfileClientReportPageParams = {
    clientGuid: string;
}

export function ClientProfileClientReportPage({clientGuid}: ClientProfileClientReportPageParams) {
     const location = useLocation();

    const [{
        selected,
        showViewModal,
        showNewModal,
        pagination,
        reportStatusFilter,
        reportTypeFilter,
        applyGroupBySfOperationCase,
        sortBy,
    }, dispatch] = React.useReducer(clientReportReducer, initState(useSearchParam().get('createReport') !== null));

    const {
        loading,
        data,
        error,
        refetch
    } = useQuery<{ clientReportsQuery: ClientReportsQueryResponse, authorizationsByClientGuidQuery: AuthorizationListResponse }, {}>(CLIENT_REPORTS_QUERY, {
        fetchPolicy: "no-cache",
        skip: !location.pathname.includes(CLIENT_PROFILE_CLIENT_REPORTS_PATH),
        variables: {
            clientGuid: clientGuid,
            params: {
                reportStatus: reportStatusFilter === "" ? null : reportStatusFilter,
                reportType: reportTypeFilter === "" ? null : reportTypeFilter,
                applyGroupBySfOperationCase: applyGroupBySfOperationCase,
                sortDir: sortBy.order,
                sortBy: sortBy.column,
            },
            ...pagination.toGraphQlParams()
        }
    })

    const totalClients = data?.clientReportsQuery?.totalElements ?? 0;
    const {onPaginationClick} = useRelayPagination(dispatch, totalClients);
    let clientReports = new Array<ClientReportInfo>();
    if (data?.clientReportsQuery) {
        clientReports = data?.clientReportsQuery.edges.map((edge) => edge.node);
    }

    // always refetch if user select clientReports tab
    useEffect(() => {
        if(location.pathname.includes("clientReports")) {
           refetch();
        }
    }, [location, refetch]);

    if (error) {
        return <ErrorDisplay error={error} title="Error loading client reports" />;

    }

    return (
        <div className="h-full relative">
            <div className="h-full p-5 bg-white shadow-md flex flex-col">
                <div className="flex-1 min-h-0 w-full relative flex flex-col">
                    <div className="flex text-atlantis-200 items-center">
                        <div className="pl-2">
                            <SimpleDropdown
                                options={ClientReportReportTypeMapWithDefault}
                                selected={reportTypeFilter}
                                minWidth={"2rem"}
                                textColor={"atlantis-200"}
                                isFilterDuplicatedLabel
                                setSelected={(selected) => dispatch({
                                    type: 'SET_REPORT_TYPE_FILTER',
                                    reportTypeFilter: selected
                                })}/>
                        </div>
                        <div className="pl-2">
                            <SimpleDropdown
                                options={ClientReportReportStatusMapWithDefault}
                                selected={reportStatusFilter}
                                minWidth={"2rem"}
                                textColor={"atlantis-200"}
                                setSelected={(selected) => dispatch({
                                    type: 'SET_REPORT_STATUS_FILTER',
                                    reportStatusFilter: selected
                                })}/>
                        </div>
                        <div className="pl-3 flex">
                            <Switch
                                height={24}
                                checked={applyGroupBySfOperationCase}
                                onChange={(e) => dispatch({
                                    type: 'TOGGLE_GROUP_BY_SF_OPERATION_CASE'
                                })}
                            />
                            <div className="pl-2 text-atlantis-200 text-sm font-semibold">Group by Report Reference</div>
                        </div>
                    </div>
                    <Pane display='flex' justifyContent='flex-end' paddingBottom={8}>
                        <RelayPagination pageInfo={data?.clientReportsQuery.pageInfo} count={totalClients}
                                         onClick={onPaginationClick}/>
                    </Pane>
                    <ClientProfileReportTableFull reports={clientReports}
                                                  selected={selected}
                                                  dispatch={dispatch}
                                                  loading={loading}
                                                  sortBy={sortBy}
                                                  isApplyGroupBySfOperationCase={applyGroupBySfOperationCase}/>
                    {
                        selected != null &&
                        <div className='absolute bottom-0 right-0 flex' style={{width: 300}}>
                            <FatOutlineButton color={'black'}
                                              text={'Unselect'}
                                              onClick={() => dispatch({type: 'SET_SELECTED', selected: null})}/>
                            <div style={{width: 10}}/>
                            <FatOutlineButton color={'green'} text={"View Detail"}
                                              onClick={() => dispatch({type: 'SHOW_VIEW_MODAL'})}
                            />
                        </div>
                    }
                </div>
            </div>
            <ClientReportNewModal showModal={showNewModal} dispatch={dispatch} clientGuid={clientGuid} refetch={refetch}
                                  authorizationList={data?.authorizationsByClientGuidQuery ?? []}/>
            <ClientReportViewModal readonly
                    showModal={showViewModal}
                    hideModalFunc={() => dispatch({type: 'HIDE_VIEW_MODAL'})}
                    afterRemovalFunc={() => {
                        refetch?.();
                        dispatch({type: 'SET_SELECTED', selected: null});
                        dispatch({type: 'HIDE_VIEW_MODAL'});
                    }}
                    refetch={refetch}
                    report={selected}/>
        </div>
    );
}
